import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { Quotes } from "../Quotes";
import { BankStatementsBlock } from "../BidInfoBlocks/BankStatements/BankStatementsBlock";
import { WalletCheckList } from "../WalletCheckList";
import { TransactionList } from "../TransactionList";
import { BidFilesBlock } from "../BidFilesBlock";
import { CustomTabItem, CustomTabs } from "ui/CustomTabs";
import { getBidFilesBlockProps } from "../BidFilesBlock/getBidFilesBlockProps";
import { useEffect, useMemo, useState } from "react";

import { Spin } from "antd";
import { getDefaultActiveTab } from "../helpers";

interface BidInfoTabsProps {
  showQuotes: boolean;
  showBankDetails: boolean;
  showWalletCheck: boolean;
  showTransactionList: boolean;
  onWalletChecksUpdated: () => Promise<void>;
}

export const BidInfoTabs = observer(
  ({ showQuotes, showBankDetails, showWalletCheck, showTransactionList, onWalletChecksUpdated }: BidInfoTabsProps) => {
    const { bidCard, quotes, user, bidTransactions, bidWalletChecks } = useStores();
    const { isCustomer, isProvider, isLPRiskManager } = user.roles;
    const [activeKey, setActiveKey] = useState("");

    const transactionsCount = bidTransactions.allData.length ?? 0;
    const quotesCount = quotes.paginationData.totalElements ?? 0;
    const walletChecksCount = bidWalletChecks.allData.length ?? 0;

    const bidFilesBlockProps = getBidFilesBlockProps({
      bidData: bidCard.bid,
      user,
    });

    const loading = [
      bidCard.fetchStatus,
      quotes.fetchStatus,
      bidTransactions.fetchStatus,
      !isCustomer && !isProvider && !isLPRiskManager ? bidWalletChecks.fetchStatus : undefined,
    ].some((status) => status && !["error", "success", "init"].includes(status));

    const defaultActiveKey = useMemo(() => {
      return getDefaultActiveTab({
        stores: { bidCard, user, bidTransactions, bidWalletChecks },
        showQuotes,
      });
    }, [bidCard.bid.status, user.roles, bidTransactions.fetchStatus, bidWalletChecks.fetchStatus]);

    const items: CustomTabItem[] = [];
    if (showQuotes) {
      items.push({
        key: "quotes",
        title: "Котировки",
        count: quotesCount,
        children: <Quotes />,
      });
    }

    if (showBankDetails) {
      items.push({
        key: "bankStatements",
        title: "Платежная информация",
        children: <BankStatementsBlock />,
      });
    }

    if (showTransactionList) {
      items.push({
        key: "transactions",
        title: "Транзакции",
        count: transactionsCount,
        children: <TransactionList />,
      });
    }

    items.push({
      key: "documents",
      title: "Документы",
      count: bidFilesBlockProps.totalDocumentsCount,
      children: <BidFilesBlock {...bidFilesBlockProps} />,
    });

    if (showWalletCheck) {
      items.push({
        key: "walletCheck",
        title: "Проверка кошельков",
        count: walletChecksCount,
        children: <WalletCheckList onWalletChecksUpdated={onWalletChecksUpdated} />,
      });
    }

    return (
      <Spin spinning={loading}>
        <CustomTabs
          items={items}
          defaultActiveKey={defaultActiveKey}
          onTabClick={(newTab) => {
            bidCard.setActiveTab(newTab);
          }}
          activeKey={bidCard.activeTab ?? defaultActiveKey}
        />
      </Spin>
    );
  }
);
